import React, { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { fetchTickets } from '../components/api';
import { useUser } from '../context/UserContext';

const COFeedback = ({ userClaims, isEmbedded = false }) => {
  const { user } = useUser();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const allowedCountriesIso3 = user?.allowedCountriesIso3;
  const allowedCountriesKey = Array.isArray(allowedCountriesIso3) ? allowedCountriesIso3.join(',') : '';
  const multipleCountries = Array.isArray(allowedCountriesIso3)
    ? (allowedCountriesIso3.length !== 1 || allowedCountriesIso3.includes('global'))
    : true;
  const singleCountry = Array.isArray(allowedCountriesIso3) && !multipleCountries && allowedCountriesIso3[0]
    ? allowedCountriesIso3[0]
    : null;
  const countryNameByIso3 = useMemo(() => {
    const names = Array.isArray(user?.allowedCountries) ? user.allowedCountries : [];
    const iso3s = Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    const lookup = new Map();

    iso3s.forEach((iso3, index) => {
      const code = String(iso3 || '').trim().toUpperCase();
      const name = String(names[index] || '').trim();
      if (code && name && name.toLowerCase() !== 'global') {
        lookup.set(code, name);
      }
    });

    const userCountryCode = String(user?.country_iso3 || '').trim().toUpperCase();
    const userCountryName = String(user?.country || '').trim();
    if (userCountryCode && userCountryName) {
      lookup.set(userCountryCode, userCountryName);
    }

    return lookup;
  }, [user?.allowedCountries, user?.allowedCountriesIso3, user?.country, user?.country_iso3]);

  useEffect(() => {
    const loadTickets = async () => {
      try {
        if (!Array.isArray(allowedCountriesIso3)) return;
        setLoading(true);
        setError(null);

        const batchSize = 500;
        let offset = 0;
        let allTickets = [];

        while (true) {
          const chunk = await fetchTickets(userClaims.role, singleCountry, { limit: batchSize, offset });
          allTickets = allTickets.concat(chunk || []);
          if (!chunk || chunk.length < batchSize) break;
          offset += batchSize;
        }

        setTickets(allTickets);
      } catch (err) {
        console.error('Error loading ticket summary:', err);
        setError('Failed to load ticket summary. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadTickets();
  }, [userClaims.role, singleCountry, allowedCountriesIso3, allowedCountriesKey]);

  const summaryRows = useMemo(() => {
    const byCountry = new Map();

    tickets.forEach((ticket) => {
      const rawCountry = String(ticket.country || ticket.country_code || '').trim();
      const countryCode = String(ticket.country_code || ticket.country || '').trim().toUpperCase();
      const country = countryNameByIso3.get(countryCode) || rawCountry || 'Unknown';
      if (!byCountry.has(country)) {
        byCountry.set(country, {
          country,
          open: 0,
          closed: 0,
          low: 0,
          medium: 0,
          high: 0,
          critical: 0,
        });
      }

      const row = byCountry.get(country);
      const status = String(ticket.status || '').trim().toLowerCase();
      const priority = String(ticket.priority || '').trim().toLowerCase();

      if (status === 'open') row.open += 1;
      if (status === 'closed') row.closed += 1;

      if (priority === 'low') row.low += 1;
      if (priority === 'medium') row.medium += 1;
      if (priority === 'high') row.high += 1;
      if (priority === 'critical') row.critical += 1;
    });

    return Array.from(byCountry.values()).sort((a, b) => a.country.localeCompare(b.country));
  }, [countryNameByIso3, tickets]);

  const content = (
    <div className="p-4">
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-800">CO Feedback - Ticket Summary by Country</h3>
          {!loading && !error && (
            <span className="text-sm text-gray-600">Countries: {summaryRows.length}</span>
          )}
        </div>

        {loading && (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 rounded px-4 py-3">
            {error}
          </div>
        )}

        {!loading && !error && (
          summaryRows.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
              No tickets available for summary.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Country Name</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Open</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Closed</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Low</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Medium</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">High</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Critical</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summaryRows.map((row) => (
                    <tr key={row.country} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{row.country}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.open}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.closed}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.low}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.medium}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.high}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.critical}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="co-feedback" userClaims={userClaims} />
      {content}
    </div>
  );
};

export default COFeedback; 